// src/App.jsx
import { Routes, Route, Navigate, Outlet, useParams, useLocation } from 'react-router-dom';
import { looksLikeActivationCode } from './utils/activationCode';
import { Suspense, lazy, useEffect } from 'react';
import { SoloFlowProvider, useSoloFlow } from './contexts/SoloFlowProvider';
import FullscreenLayout from './components/FullscreenLayout';
import LoadingScreen from './components/shared/LoadingScreen';
import ErrorBoundary from './components/shared/ErrorBoundary';
import { ConnectivityBanner, ConnectivityIcon } from './components/shared/ConnectivityBanner';
import { lazyWithRetry, clearChunkReloadFlag } from './utils/lazyWithRetry';
import { ROUTE_IMPORTS, warmRoutes } from './utils/routePrefetch';
import { Toaster } from 'sonner';

// Lazy load pages for code splitting. `lazyWithRetry` rather than bare `lazy`:
// a deploy during a live tasting invalidates the content-hashed chunks this tab
// is holding, and an unhandled `import()` rejection is a white screen the player
// can never get out of.
//
// The factories come from routePrefetch so that the router and the warming
// below load the SAME chunks — see that file for why the navigation between
// screens used to cost a round trip.
const SessionEntry = lazyWithRetry(ROUTE_IMPORTS.SessionEntry);
const Landing = lazyWithRetry(ROUTE_IMPORTS.Landing);
const Join = lazyWithRetry(ROUTE_IMPORTS.Join);
const AvatarSelect = lazyWithRetry(ROUTE_IMPORTS.AvatarSelect);
const WineSelect = lazyWithRetry(ROUTE_IMPORTS.WineSelect);
const Question = lazyWithRetry(ROUTE_IMPORTS.Question);
const Rating = lazyWithRetry(ROUTE_IMPORTS.Rating);
const AnswerComparison = lazyWithRetry(ROUTE_IMPORTS.AnswerComparison);
const WineReveal = lazyWithRetry(ROUTE_IMPORTS.WineReveal);
const ThankYou = lazyWithRetry(ROUTE_IMPORTS.ThankYou);
const LeaderboardDisplay = lazyWithRetry(ROUTE_IMPORTS.LeaderboardDisplay);
// Design preview of the activation TV, fed demo data. Development only: the
// condition is replaced at build time, so the page and its chunk are absent
// from a production bundle.
const DevPreviewBoard = import.meta.env.DEV
  ? lazy(() => import('./pages/DevPreviewBoard'))
  : null;

// Where each screen leads, keyed by the last segment of its path. Warmed while
// the player is still on the screen BEFORE it, so the navigation itself has no
// chunk left to fetch. It lives next to the route table deliberately: a route
// renamed below without a thought for this map is an obvious, local mistake.
//
// `''` covers both entry points — `/` and the `/:code` index, whose last
// segment is the activation code itself.
const NEXT_CHUNKS = {
  '': ['Landing', 'Join'],
  landing: ['Join', 'AvatarSelect'],
  join: ['AvatarSelect', 'WineSelect'],
  avatar: ['WineSelect', 'Question'],
  wines: ['Question', 'Rating'],
  question: ['Rating', 'WineReveal'],
  rating: ['WineReveal', 'AnswerComparison'],
  reveal: ['AnswerComparison', 'ThankYou'],
  comparison: ['ThankYou', 'WineSelect'],
};

const page = (Component) => (
  <FullscreenLayout>
    <Component />
  </FullscreenLayout>
);

/**
 * Layout route for the `/:code` tree. Syncs the activation code from the URL
 * into context so direct deep links and refreshes hydrate the session.
 *
 * It also guards the root namespace, which is the job the old `/s/` segment
 * used to do for free. With the code at the root, `/:code` is the broadest
 * route in the app: anything that is not a page name reaches it. A path that
 * cannot be a code is sent back to the entry screen rather than spending a
 * round trip asking the server about `favicon.ico`.
 */
function SessionSync() {
  const { code } = useParams();
  const { sessionCode, setSessionCode } = useSoloFlow();
  const valid = looksLikeActivationCode(code);

  useEffect(() => {
    if (valid && code.toUpperCase() !== sessionCode) {
      setSessionCode(code.toUpperCase());
    }
  }, [valid, code, sessionCode, setSessionCode]);

  if (!valid) return <Navigate to="/" replace />;
  return <Outlet />;
}

/**
 * The old `/s/:code` links, kept working.
 *
 * Those URLs are printed on the QR codes sitting on venue tables, and on
 * whatever has already been pasted into group chats. Dropping the prefix
 * without this would 404 every table that already has a card on it, which is
 * not a thing a deploy is allowed to do. The whole tail is carried across, so
 * `/s/ABCD12/board` — the venue screen someone has bookmarked — lands on
 * `/ABCD12/board` rather than at the entry form.
 */
function LegacyPrefixRedirect() {
  const { '*': rest } = useParams();
  const { search, hash } = useLocation();
  return <Navigate to={`/${rest || ''}${search}${hash}`} replace />;
}

/**
 * The routed part of the app, wrapped in a boundary that resets on navigation —
 * so a screen that throws doesn't leave every screen after it broken too.
 */
function AppRoutes() {
  const location = useLocation();

  // We got here, so this build's chunks are loading fine. Release the one-shot
  // reload guard for whatever the NEXT deploy does.
  useEffect(() => {
    clearChunkReloadFlag();
  }, []);

  // Pull the next screens' chunks down in the background. Keyed off the path
  // rather than off each page, so there is one place to look.
  useEffect(() => {
    const step = location.pathname.replace(/\/+$/, '').split('/').pop();
    warmRoutes(...(NEXT_CHUNKS[step] || NEXT_CHUNKS['']));
  }, [location.pathname]);

  return (
    <ErrorBoundary scope="route" resetKey={location.pathname}>
      <Suspense fallback={<LoadingScreen loading message="Loading..." />}>
        <Routes>
          {/* Bare entry — enter activation code manually */}
          <Route path="/" element={page(SessionEntry)} />

          {DevPreviewBoard && <Route path="/dev/board" element={<DevPreviewBoard />} />}

          {/* Where the flow used to live. Redirects, never renders. */}
          <Route path="/s/*" element={<LegacyPrefixRedirect />} />

          {/* Full flow lives under the activation code so it stays in the URL */}
          <Route path="/:code" element={<SessionSync />}>
            <Route index element={page(SessionEntry)} />
            <Route path="landing" element={page(Landing)} />
            <Route path="join" element={page(Join)} />
            <Route path="avatar" element={page(AvatarSelect)} />
            <Route path="wines" element={page(WineSelect)} />
            <Route path="question" element={page(Question)} />
            <Route path="rating" element={page(Rating)} />
            <Route path="comparison" element={page(AnswerComparison)} />
            <Route path="reveal" element={page(WineReveal)} />
            {/* Player's final screen — thank you + buy a tasting box CTA */}
            <Route path="finish" element={page(ThankYou)} />
            {/* Persistent leaderboard for a second screen (venue TV / tablet) */}
            <Route path="board" element={page(LeaderboardDisplay)} />
          </Route>

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </ErrorBoundary>
  );
}

/**
 * Connection state: the icon confirms a healthy link at a glance, the banner
 * explains a bad one — and says explicitly that answers are held locally
 * rather than lost.
 *
 * Both are for the player's phone. The venue board is not a phone: it holds
 * nobody's answers, it only reads, and its top-right corner is where the
 * Tasted mark and an activation's partner logos sit. A signal glyph pinned
 * over a sponsor's logo is the wrong thing in the wrong place.
 */
function ConnectivityChrome() {
  const { pathname } = useLocation();
  if (pathname.endsWith('/board')) return null;
  return (
    <>
      <ConnectivityIcon />
      <ConnectivityBanner />
    </>
  );
}

export default function App() {
  return (
    <SoloFlowProvider>
      <Toaster richColors position="top-center" />
      <ConnectivityChrome />
      <AppRoutes />
    </SoloFlowProvider>
  );
}
